// ClientHub access — the workspace lens (Epic 2 · S2.6).
//
// The wall moved from roles into the database. A person who holds a workspace
// (fn_my_workspaces returns at least one entry) can access ClientHub; a person
// who doesn't, can't — regardless of their legacy role number. The menu is a
// convenience, not the wall: every function checks auth.uid() itself, so a URL
// for a workspace someone doesn't hold returns nothing regardless.
//
// This replaces the former role-based gate (canAccessHub checked roles 0/1/2).
// The role-based predicate is preserved for the transition so pages that import
// it keep working, but the hook now reads from the workspace provider.

import { useWorkspace } from '../../contexts/WorkspaceContext';
import { useActiveRole } from '../../hooks/useActiveRole';

/**
 * Legacy role-based predicate — preserved for the transition. Prefer
 * `useHubAccess()` (workspace-based) for new code.
 * @param {{ isPlatformAdmin?: boolean, isAdmin?: boolean, isManager?: boolean }} role
 * @returns {boolean}
 */
export const canAccessHub = (role) =>
  !!(role && (role.isPlatformAdmin || role.isAdmin || role.isManager));

/**
 * Client Hub navigation visibility. Hidden from the main nav for all users
 * (see the note below). Reachable by direct URL and via the Profile page entry
 * for anyone who holds a workspace.
 */
export const CLIENT_HUB_NAV_VISIBLE = false;

/**
 * Hook — workspace-based access. Returns true when the caller holds at least
 * one workspace (fn_my_workspaces returned a non-empty array). This replaces
 * the legacy role check: the lens, not a role flag, decides access. Falls back
 * to the role-based check while the workspace RPC is loading or errored, so a
 * transient failure never locks out a current user.
 */
export function useHubAccess() {
  const { workspaces, loading, error } = useWorkspace();
  const role = useActiveRole();

  // While loading or on error, fall back to the legacy role check so access
  // never flickers off during the initial fetch.
  if (loading || error) {
    return canAccessHub(role);
  }

  // The database decides: a person with at least one workspace can access hub.
  return workspaces.length > 0;
}

/**
 * Hook — whether the Profile page "Client Hub" entry should render. Derived
 * from fn_my_workspaces (at least one workspace), NOT from role === 0.
 * Replaces the former `isSuperAdmin` check (Epic 2 · S2.6).
 */
export function useHubEntryVisible() {
  const { workspaces, loading, error } = useWorkspace();
  const role = useActiveRole();

  if (loading || error) {
    return role.isSuperAdmin;
  }

  return workspaces.length > 0;
}

